import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, brl, formatApiError } from "@/lib/api";

const EMPTY = {
  title: "",
  category: "Design",
  price: 40,
  delivery_days: 3,
  description: "",
  image_url: "",
  featured: false,
  active: true,
};

export default function ServicesTab() {
  const [services, setServices] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/services")
      .then((r) => setServices(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) window.location.reload();
        else toast.error(formatApiError(e));
      });

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setDialogOpen(true);
  };

  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.price) {
      toast.error("Informe título e preço.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), delivery_days: Number(form.delivery_days) };
      if (editing) await api.put(`/admin/services/${editing.id}`, payload);
      else await api.post("/admin/services", payload);
      toast.success(editing ? "Serviço atualizado." : "Serviço criado.");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remover "${s.title}"? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/services/${s.id}`);
      toast.success("Serviço removido.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const inputCls =
    "w-full rounded-xl border border-input bg-white px-4 py-2.5 text-sm outline-none focus:border-accent";

  if (services === null) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-ink-3" data-testid="admin-services-loading" />
      </div>
    );
  }

  return (
    <div data-testid="admin-services-list">
      <div className="mb-6 flex justify-end">
        <button
          data-testid="admin-service-new-button"
          onClick={openNew}
          className="btn-lift flex items-center gap-2 rounded-full bg-ink px-6 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-paper hover:bg-accent"
        >
          <Plus className="h-4 w-4" /> Novo serviço
        </button>
      </div>
      <div className="flex flex-col gap-3">
        {services.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-line bg-white p-5" data-testid={`admin-service-card-${s.id}`}>
            <div className="flex items-center gap-4">
              {s.image_url ? (
                <img src={s.image_url} alt="" className="h-14 w-20 rounded-lg object-cover" />
              ) : (
                <div className="flex h-14 w-20 items-center justify-center rounded-lg bg-paper2 font-display text-xl text-ink-3">E</div>
              )}
              <div>
                <p className="font-display text-base font-bold">{s.title}</p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-widest text-ink-3">
                  {s.category} · {brl(s.price)} · {s.delivery_days} dias úteis
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${
                  s.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600"
                }`}
                data-testid={`admin-service-active-${s.id}`}
              >
                {s.active ? "Ativo" : "Inativo"}
              </span>
              <button
                data-testid={`admin-service-edit-${s.id}`}
                onClick={() => openEdit(s)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line hover:border-ink"
                aria-label={`Editar ${s.title}`}
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`admin-service-delete-${s.id}`}
                onClick={() => remove(s)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-red-500 hover:border-red-500"
                aria-label={`Remover ${s.title}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl bg-paper sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">
              {editing ? "Editar serviço" : "Novo serviço"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <input data-testid="admin-service-form-title" placeholder="Título" value={form.title} onChange={setField("title")} className={inputCls} />
            <input data-testid="admin-service-form-category" placeholder="Categoria" value={form.category} onChange={setField("category")} className={inputCls} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="admin-service-form-price" type="number" min="40" max="200" step="0.01" placeholder="Preço (R$ 40–200)" value={form.price} onChange={setField("price")} className={inputCls} />
              <input data-testid="admin-service-form-delivery" type="number" min="1" placeholder="Dias úteis" value={form.delivery_days} onChange={setField("delivery_days")} className={inputCls} />
            </div>
            <textarea data-testid="admin-service-form-description" placeholder="Descrição" value={form.description} onChange={setField("description")} rows={3} className={`${inputCls} resize-none`} />
            <input data-testid="admin-service-form-image" placeholder="URL da imagem" value={form.image_url} onChange={setField("image_url")} className={inputCls} />
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="admin-service-form-active" type="checkbox" checked={form.active} onChange={setField("active")} className="h-4 w-4 accent-[#E05A47]" />
              Ativo (visível na vitrine)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input data-testid="admin-service-form-featured" type="checkbox" checked={form.featured} onChange={setField("featured")} className="h-4 w-4 accent-[#E05A47]" />
              Destaque (imagem grande)
            </label>
            <button
              data-testid="admin-service-form-submit"
              onClick={save}
              disabled={saving}
              className="btn-lift mt-2 flex items-center justify-center gap-2 rounded-full bg-ink py-3.5 font-mono text-[10px] uppercase tracking-[0.2em] text-paper hover:bg-accent disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {editing ? "Salvar alterações" : "Criar serviço"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
